import { formatArray } from "./formatArray";
import {
  enumCellObjectSubtypes,
  enumFieldType,
  enumHyperLinkType,
  kBlockedFieldTag
} from "../constants";
import { PDecimalPlaces } from "./PDecimalPlaces";
import { ConvertToDate } from "./ConvertToDate";
import { appstr } from "./appstr.js";

/**
 * Blocked-field sentinel utilities.
 *
 * The backend writes `kBlockedFieldTag` ("__blocked__") into any field value
 * the current user is not allowed to see (via Row-Level Security).
 * The frontend must never display that raw sentinel. Instead:
 *
 *   1. `isBlockedField(value)` - detect the sentinel
 *   2. `getBlockedFieldDisplayValue()` - get the localized display string
 *      (resolved via `appstr` from QENStrings.js, currently "(hidden)")
 *   3. `sanitizeBlockedValue(value, fallback)` - one-step detect-and-replace
 *   4. `FormatFieldValue(...)` - general formatter; returns the display string
 *      automatically when it encounters the sentinel
 *
 * @see kBlockedFieldTag in constants.js
 * @see QENStrings.js for the localized "(hidden)" mapping
 */
export function isBlockedField(value) {
  return value === kBlockedFieldTag;
}

export function getBlockedFieldDisplayValue() {
  return appstr(kBlockedFieldTag);
}

export function sanitizeBlockedValue(value, fallback) {
  if (isBlockedField(value)) return getBlockedFieldDisplayValue();
  return fallback !== undefined ? (value ?? fallback) : value;
}

// getBlockedFieldDisplayValue() is already "(hidden)" don't wrap again
export function appendParenthesizedValue(label, formattedValue) {
  if (formattedValue === getBlockedFieldDisplayValue()) return `${label} ${formattedValue}`;
  return `${label} (${formattedValue})`;
}

/** Map display value back to raw sentinel so server queries match raw data. */
export function toServerFilterValue(value) {
  if (value === getBlockedFieldDisplayValue()) return kBlockedFieldTag;
  return value;
}

/** Deduplicate filter results, normalizing blocked sentinels to display value. */
export function deduplicateFilterValues(results, fieldName) {
  const seen = new Set();
  const data = [];
  for (const item of results ?? []) {
    const value = item[fieldName];
    const mapped = isBlockedField(value) ? getBlockedFieldDisplayValue() : (value ?? "");
    if (!seen.has(mapped)) {
      seen.add(mapped);
      data.push(mapped);
    }
  }
  return data;
}

export function FormatFieldValue(
  value,
  fieldDef,
  boxCellDesc,
  truncateListAfter = 5,
  compactFormat = false
) {
  //TODO: These should be scoped at the field def with a value input argument...
  if (!fieldDef) return value;
  if (isBlockedField(value) || value === getBlockedFieldDisplayValue())
    return getBlockedFieldDisplayValue();
  var fieldType = fieldDef.GetFieldType();
  var subType = fieldDef.GetFieldSubtype();

  if (subType === enumCellObjectSubtypes.list && Array.isArray(value)) {
    const formattedValues = [];
    const max = truncateListAfter > 0 ? truncateListAfter : value.length;
    const values = value.slice(0, max);
    for (const v of values) {
      formattedValues.push(
        FormatFieldValue(v, fieldDef, boxCellDesc, truncateListAfter, compactFormat)
      );
    }

    let valueBuilder = formattedValues.join(", ");
    if (value.length > max) {
      valueBuilder = `${valueBuilder} (+${value.length - max} ${appstr("More").toLowerCase()})`;
    }

    return valueBuilder;
  }

  switch (fieldType) {
    case enumFieldType.object:
      switch (subType) {
        case enumCellObjectSubtypes.alsoreportsto:
          if (Array.isArray(value)) return formatArray(value.map(y => y["Name"]));
          if (typeof value === "string") return value;
          return "";
        case enumCellObjectSubtypes.objinfo:
          var outStr = "";
          if (value["source"]) {
            var sourceInfo = value["source"];
            outStr += sourceInfo["type"];
            if (sourceInfo["details"]) {
              outStr += " (" + sourceInfo["details"] + ")";
            } else {
              outStr += " (" + sourceInfo["name"] + ")";
            }
          }
          if (value["modified"]) {
            for (var aModIfx in value["modified"]) {
              outStr += " (" + sourceInfo["modified"] + ")";
            }
          }
          return outStr;
        default:
          return value;
      }
      break;
    case enumFieldType.text:
      var linkType = fieldDef.GetHyperlinkType();
      if (linkType == enumHyperLinkType.hyperlink || linkType == enumHyperLinkType.file) {
        if (value == null) {
          value = "";
        } else {
          var s = value.split("|");
          value = s[0];
        }
      }
      break;
    case enumFieldType.number:
      var preV = fieldDef.GetPrefix();
      if (preV == null) preV = "";
      var postV = fieldDef.GetPostfix();
      if (postV == null) postV = "";
      var showP = fieldDef.GetShowPercent();
      var showT = fieldDef.GetShowThousandsSep();
      if (typeof value == "string") {
        value = value.replaceAll(",", "");
      }
      var n = parseFloat(value);
      var perc = "";
      if (isNaN(n)) n = 0;
      if (showP) {
        n = n * 100;
        perc = "%";
      }

      var places = fieldDef.GetNumDecPlaces();

      if (compactFormat && !showP) {
        const compactOptions = {
          notation: "compact",
          compactDisplay: "short",
          minimumFractionDigits: 0,
          maximumFractionDigits: places || 0
        };
        value = preV + new Intl.NumberFormat("en-US", compactOptions).format(n) + postV;
      } else {
        value = preV + PDecimalPlaces(n, places, showT) + perc + postV;
      }
      break;
    case enumFieldType.date:
      var dateFormat = fieldDef.GetDateFormat();
      var doConvertToLocal = fieldDef.GetShowLocal(); //Dates can come in without any TZ info. We can treat these as 'not times' but still apply formatting with this. Won't interfere with date math.
      var d = ConvertToDate(value);
      if (d != null) {
        var date = new Date(d);
        if (doConvertToLocal) {
          var today = new Date();
          date.setTime(d + today.getTimezoneOffset() * 60 * 1000);
          if (today.isDstObserved()) date.addHours(1);
        }
        value = date.FormatDate(dateFormat);
      }
      break;
  }

  return value;
}
